import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { LOGO_URL, WORK_PHOTOS } from "@/constants/site";
import { useLanguage } from "@/i18n/LanguageContext";

const leaves = [
  { left: "5%", size: 22, duration: "8s", delay: "0s" },
  { left: "16%", size: 16, duration: "10s", delay: "1.2s" },
  { left: "28%", size: 26, duration: "7s", delay: "0.6s" },
  { left: "42%", size: 18, duration: "11s", delay: "2s" },
  { left: "55%", size: 24, duration: "8.5s", delay: "0.3s" },
  { left: "68%", size: 15, duration: "9.5s", delay: "1.6s" },
  { left: "80%", size: 26, duration: "7.5s", delay: "0.9s" },
  { left: "92%", size: 18, duration: "10.5s", delay: "2.4s" },
];

const Leaf = ({ left, size, duration, delay }) => (
  <svg
    className="welcome-leaf"
    style={{ left, animationDuration: duration, animationDelay: delay }}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="M12 2C7 7 4 11 4 15a8 8 0 0 0 16 0c0-4-3-8-8-13z" fill="#D4AF37" opacity="0.5" />
    <path d="M12 7v13" stroke="#1A1412" strokeWidth="1" opacity="0.45" />
  </svg>
);

export const WelcomeOverlay = () => {
  const [show, setShow] = useState(true);
  const { t } = useLanguage();

  // Gentle two-note chime (Web Audio) — played only on the visitor's tap, per browser autoplay rules
  const playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      [659.25, 987.77].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const start = ctx.currentTime + i * 0.18;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.08, start + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 1.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 1.5);
      });
    } catch (e) {
      /* audio not supported — ignore */
    }
  };

  const dismiss = () => {
    playChime();
    setShow(false);
  };

  useEffect(() => {
    const t = setTimeout(() => setShow(false), 5000);
    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden cursor-pointer"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9 }}
          data-testid="welcome-overlay"
          onClick={dismiss}
        >
          <img src={WORK_PHOTOS.entrance} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1A1412]/85 backdrop-blur-[2px]" />

          {leaves.map((l, i) => (
            <Leaf key={i} {...l} />
          ))}

          <button
            className="absolute top-6 right-6 z-20 p-3 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#1A1412] transition-colors duration-300"
            onClick={(e) => {
              e.stopPropagation();
              dismiss();
            }}
            aria-label={t.aria.closeWelcome}
            data-testid="welcome-close-button"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="relative z-10 text-center px-6 max-w-2xl">
            <motion.img
              src={LOGO_URL}
              alt="The Build and Design logo"
              className="h-24 w-24 object-cover rounded-sm mx-auto border border-[#D4AF37]/40"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
            />
            <motion.p
              className="font-hindi text-6xl sm:text-7xl text-[#D4AF37] mt-8 leading-tight"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              data-testid="welcome-namaste"
            >
              नमस्ते
            </motion.p>
            <motion.p
              className="font-heading text-3xl sm:text-4xl text-[#FDFBF7] mt-3"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              data-testid="welcome-hello"
            >
              Hello &amp; Welcome
            </motion.p>
            <motion.p
              className="font-hindi text-lg text-[#B8A99A] mt-5"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              data-testid="welcome-hindi-line"
            >
              द बिल्ड एंड डिज़ाइन में आपका स्वागत है
            </motion.p>
            <motion.p
              className="text-[#B8A99A] tracking-[0.35em] uppercase text-xs mt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1 }}
            >
              {t.welcome.tagline}
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
